import { getAvatar } from "../helper";
import { Calendar, CheckCircle } from "lucide-react";

export type SkipRequestProps = {
  skipRequests: SkipRequest[];
  isVisible: boolean;
  handleSkipRequest: (id: string, status: "approved" | "denied") => void;
};

type SkipRequest = {
  id: string;
  studentName: string;
  wordEnglish: string;
  wordLocal: string;
  category: string;
  level?: string;
  status: "pending" | "approved" | "denied";
  timestamp: string;
  teacherNote?: string;
};

const SkipRequest = (props: {
  skipRequests: SkipRequest[];
  isVisible: boolean;
  handleSkipRequest: (id: string, status: "approved" | "denied") => void;
}) => {
  const { skipRequests, isVisible, handleSkipRequest } = props;

  return (
    <div
      className={`space-y-6 transform transition-all duration-1000 delay-600 ${
        isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
      }`}
    >
      {skipRequests.map((request, index) => (
        <div
          key={request.id}
          className={`bg-white/30 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/40 overflow-hidden transform transition-all duration-300 hover:shadow-3xl hover:scale-[1.02] ${
            request.status === "pending"
              ? "bg-yellow-50/30"
              : request.status === "denied"
              ? "bg-red-50/30"
              : "bg-green-50/30"
          }`}
          style={{
            animationDelay: `${index * 200}ms`,
            animation: isVisible ? "slideInUp 0.6s ease-out forwards" : "none",
          }}
        >
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className={`w-12 h-12 ${getAvatar(
                    request.studentName
                  )} rounded-full flex items-center justify-center text-white font-semibold shadow-lg`}
                >
                  😊
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 text-lg">
                    {request.studentName}
                  </h3>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        request.status === "pending"
                          ? "bg-yellow-100 text-yellow-700"
                          : request.status === "denied"
                          ? "bg-red-100 text-red-700"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {request.status === "pending" && (
                        <Calendar className="w-3 h-3" />
                      )}
                      {request.status === "denied" && "❌"}
                      {request.status === "approved" && "✅"}
                      {request.status.charAt(0).toUpperCase() +
                        request.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-right text-sm text-slate-600">
                <div>{request.timestamp}</div>
              </div>
            </div>

            {/* Word Section */}
            <div className="bg-white/40 rounded-2xl p-4 mb-4 border border-white/50">
              <div className="text-sm text-slate-600 mb-2">Word to skip:</div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-purple-600 mb-1">
                    {request.wordEnglish}
                  </div>
                  <div className="text-xl text-slate-700">
                    {request.wordLocal}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-slate-600 mb-1">Category:</div>
                  <div className="font-semibold text-slate-800">
                    {request.category}
                  </div>
                  {request.level && (
                    <div className="text-sm text-slate-600 mt-1">
                      Level: {request.level}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Teacher Note */}
            {/* {request.teacherNote && (
                <div className="bg-blue-50/50 rounded-xl p-3 mb-4 border border-blue-200/50">
                  <div className="text-sm font-semibold text-blue-700 mb-1">Teacher Note:</div>
                  <div className="text-sm text-blue-600">{request.teacherNote}</div>
                </div>
              )} */}

            {/* Action Buttons - Only show for pending requests */}
            {request.status === "pending" && (
              <div className="flex gap-3">
                <button
                  onClick={() => handleSkipRequest(request.id, "approved")}
                  className="flex-1 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  <CheckCircle className="w-5 h-5" />
                  Approve
                </button>
                <button
                  onClick={() => handleSkipRequest(request.id, "denied")}
                  className="flex-1 flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white py-3 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  ❌ Deny
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SkipRequest;
